/**
 * Secrets Collective — cart drawer recommendation cards.
 *
 * Size selection and the add. Everything about which products appear is
 * decided in Liquid, so the row re-evaluates server-side on every cart change
 * through the Section Rendering API and can never drift from the cart.
 */

import { addItems } from './sc-bundle.js';

class SCRec extends HTMLElement {
  /*
   * Same reason as sc-cart-promos: the drawer is morphed, so this host survives
   * a cart update while its buttons are replaced. Listeners bound to those
   * buttons die with them and connectedCallback never fires again, which is why
   * the size chips and Add button went dead until a page refresh. Delegate to
   * the host and look children up on demand.
   */
  #bound = false;

  connectedCallback() {
    this.variants = this.#json('variants');
    this.sizeIndex = Number(this.dataset.sizeIndex) || 0;
    this.readyLabel = this.cta?.dataset.labelReady || 'Add';
    this.chooseLabel = this.chooseLabel || this.ctaText?.textContent?.trim() || 'Pick a size';

    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', this.#onClick);
    }

    this.#markUnavailable();
    this.#paint();
  }

  get cta() {
    return this.querySelector('[ref="cta"]');
  }
  get ctaText() {
    return this.querySelector('[ref="ctaText"]');
  }
  get msg() {
    return this.querySelector('[ref="msg"]');
  }
  get sizes() {
    return [...this.querySelectorAll('[data-rec-size]')];
  }
  get selectedSize() {
    return this.querySelector('[data-rec-size][aria-pressed="true"]')?.dataset.value || null;
  }
  set selectedSize(_v) {
    /* derived from the DOM */
  }

  #onClick = (event) => {
    const size = event.target.closest('[data-rec-size]');
    if (size && this.contains(size)) {
      if (size.disabled) return;
      for (const o of this.sizes) o.setAttribute('aria-pressed', String(o === size));
      this.#paint();
      return;
    }

    const cta = event.target.closest('[ref="cta"]');
    if (cta && this.contains(cta)) this.#submit();
  };

  #json(ref) {
    const el = this.querySelector(`[ref="${ref}"]`);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch {
      return [];
    }
  }

  /** Cross out sizes with no sellable variant rather than failing on add. */
  #markUnavailable() {
    if (!this.sizeIndex) return;
    this.sizes.forEach((btn) => {
      const ok = this.variants.some(
        (v) => v.options[this.sizeIndex - 1] === btn.dataset.value && v.available
      );
      btn.disabled = !ok;
      btn.classList.toggle('is-unavailable', !ok);
    });
  }

  #match() {
    if (!this.sizes.length) return this.variants.find((v) => v.available) || this.variants[0];
    if (!this.selectedSize) return null;
    return this.variants.find(
      (v) => v.options[this.sizeIndex - 1] === this.selectedSize && v.available
    );
  }

  #paint() {
    const variant = this.#match();
    if (!this.cta || !this.ctaText) return;
    this.cta.disabled = !variant;
    this.ctaText.textContent = variant ? this.readyLabel : this.chooseLabel;
  }

  async #submit() {
    const variant = this.#match();
    if (!variant || this.busy) return;

    this.busy = true;
    this.cta.disabled = true;
    const previous = this.ctaText.textContent;
    this.ctaText.textContent = 'Adding…';
    if (this.msg) {
      this.msg.textContent = '';
      this.msg.classList.remove('is-error');
    }

    const result = await addItems(this, [{ id: variant.id, quantity: 1 }]);

    this.busy = false;

    if (!result?.ok) {
      this.cta.disabled = false;
      this.ctaText.textContent = previous;
      if (this.msg) {
        this.msg.textContent = result?.message || 'Could not add to cart';
        this.msg.classList.add('is-error');
      }
      return;
    }

    // The drawer re-renders on success and this card drops out of the row,
    // so there is nothing to restore — just don't leave it looking stuck.
    this.ctaText.textContent = 'Added';
  }
}

if (!customElements.get('sc-rec')) {
  customElements.define('sc-rec', SCRec);
}
